import { X, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface MultiOption {
  id: string;
  name: string;
  color?: number | null;
}

interface Props {
  options: MultiOption[];
  value: string[] | undefined;
  onChange: (next: string[]) => void;
  placeholder?: string;
  prefix?: string; // "@" or "#"
  className?: string;
  onCreate?: () => void; // optional "+ Novo" button
  createLabel?: string;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "+ add",
  prefix = "",
  onCreate,
  createLabel = "+ Nova",
}: Props) {
  const vals = value ?? [];
  const selected = vals
    .map((id) => options.find((o) => o.id === id))
    .filter((o): o is MultiOption => !!o);

  const available = options.filter((o) => !vals.includes(o.id));

  function add(id: string) {
    if (!id || vals.includes(id)) return;
    onChange([...vals, id]);
  }
  function remove(id: string) {
    onChange(vals.filter((v) => v !== id));
  }

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((opt) => (
            <Badge
              key={opt.id}
              variant="secondary"
              className="pr-1 text-sm font-normal"
              style={
                opt.color
                  ? {
                      borderColor: `#${opt.color.toString(16).padStart(6, "0")}55`,
                    }
                  : undefined
              }
            >
              <span className="opacity-60">{prefix}</span>
              {opt.name}
              <button
                type="button"
                onClick={() => remove(opt.id)}
                className="ml-1 rounded hover:bg-destructive/20 p-0.5"
                aria-label="remove"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Select value="" onValueChange={add}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {available.length === 0 ? (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                (no more to add)
              </div>
            ) : (
              available.map((opt) => (
                <SelectItem key={opt.id} value={opt.id}>
                  {prefix} {opt.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        {onCreate && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCreate}
          >
            <Plus className="h-3.5 w-3.5" />
            {createLabel}
          </Button>
        )}
      </div>
    </div>
  );
}
